import { io } from "socket.io-client";
import { v4 as uuidv4 } from "uuid";
import { serverLink } from "../api/serverLink";
import { getStore } from "../store/store";
import { attachSocketEvents } from "./socketEvents/attachSocketEvents";
import {
  forceLogout,
  refreshAccessTokenOnce,
  REFRESH_OK,
} from "../api/axiosInstance";

let socket = null;
const sessionId = uuidv4();

/** Prefer Redux (kept in sync on refresh); fall back to localStorage for reconnect races */
function getAuthTokenForSocket() {
  return (
    getStore().getState().user?.token || localStorage.getItem("authToken")
  );
}

const setupSocketListeners = (dispatch) => {
  if (!socket) return;

  let forceDisconnected = false;

  const onConnect = () => {
    attachSocketEvents(socket, dispatch);
  };

  socket.off("connect", onConnect);
  socket.on("connect", onConnect);

  // The backend sends this for two different things: a real sign-out notice
  // (a player account signed in elsewhere / changed its password — never an
  // admin's), and "duplicate" when another connection of the same account
  // takes over the live channel, e.g. a second dashboard tab or computer.
  // Only a real notice signs out — a second tab must never log the admin out.
  const SIGN_OUT_REASONS = new Set(["signedInElsewhere", "passwordChanged"]);
  socket.on("forceLogout", (payload = {}) => {
    forceDisconnected = true;
    disconnectSocket();
    if (!SIGN_OUT_REASONS.has(payload?.reason)) return;
    // Clear auth + reset the whole store (via the /login redirect reload) so no
    // stale ticket/session state survives a forced logout.
    forceLogout();
  });

  socket.on("connect_error", (error) => {
    console.warn("Socket connect_error:", error?.message ?? error);
  });

  socket.on("disconnect", (reason) => {
    console.warn("Socket disconnected:", reason);
    if (reason === "io server disconnect" && !forceDisconnected) {
      // Server kicked us off — almost always because the access token is
      // expired (post-C2 the server no longer accepts those on the socket
      // handshake), or because this admin's access changed (backend
      // sockets/adminSockets.js): the refreshed token makes AppContainer
      // re-read the permissions. Run the same refresh path HTTP uses before
      // reconnecting; if it fails we stop, instead of looping forever
      // with a dead token.
      setTimeout(async () => {
        let token = getAuthTokenForSocket();
        if (token) {
          const refreshed = await refreshAccessTokenOnce().catch(() => null);
          if (refreshed?.status === REFRESH_OK) token = refreshed.accessToken;
        }
        if (!token || !socket) return;
        socket.auth = { token, sessionId };
        socket.connect();
      }, 3000);
    }
  });

  socket.on("reconnect_attempt", () => {
    const token = getAuthTokenForSocket();
    socket.auth = token ? { token, sessionId } : {};
    console.info("Socket reconnect_attempt with auth", socket.auth);
  });
};

export const connectSocket = (dispatch) => {
  const token = getAuthTokenForSocket();
  if (!token) return;

  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
  }

  socket = io(serverLink, {
    transports: ["websocket"],
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    // Keep-alive tuning to avoid idle disconnects
    pingInterval: 20000,
    pingTimeout: 5000,
    timeout: 10000,
    auth: {
      token,
      sessionId,
    },
  });

  setupSocketListeners(dispatch);
};

export const getSocket = () => socket;

export const disconnectSocket = () => {
  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
  }
};
